import Icons from "@/assets/icons";
import Images from "@/assets/images";
import { ActionButton } from "@/components/ActionButton";
import BottomSheet from "@/components/BottomSheet";
import { Input } from "@/components/Input";
import Select from "@/components/Select";
import Header from "@/components/ui/Header";
import { Colors } from "@/constants/Colors";
import { US_STATES } from "@/constants/data";
import UnclaimedServices from "@/services/unclaimedServices";
import { Address } from "@/types/unclaimed";
import AntDesign from "@expo/vector-icons/build/AntDesign";
import { Image } from "expo-image";
import { router } from "expo-router";
import { showError } from "@/utils/toast";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FlatList,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { scale, StyleSheet } from "@/utils/appStyleSheet";
import useWindow from "@/hooks/useWindow";

enum Step {
  Step0 = 0,
  Step1 = 1,
  Step2 = 2,
}

type FormState = {
  firstName: string;
  lastName: string;
};

type FormErrors = {
  firstName?: string;
  lastName?: string;
};

type AddressErrors = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
};

const validateName = (name: string, fieldName: string): string | undefined => {
  const trimmed = name.trim();
  if (!trimmed) {
    return `${fieldName} is required`;
  }
  if (trimmed.length < 2) {
    return `${fieldName} must be at least 2 characters`;
  }
  if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) {
    return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
  }
  return undefined;
};

const validateZipCode = (zip: string): string | undefined => {
  const trimmed = zip.trim();
  if (!trimmed) {
    return "Zip code is required";
  }
  if (!/^\d{5}(-\d{4})?$/.test(trimmed)) {
    return "Enter a valid 5-digit zip code";
  }
  return undefined;
};

const validateStreet = (street: string): string | undefined => {
  const trimmed = street.trim();
  if (!trimmed) {
    return "Street address is required";
  }
  if (trimmed.length < 5) {
    return "Enter a valid street address";
  }
  return undefined;
};

const validateCity = (city: string): string | undefined => {
  const trimmed = city.trim();
  if (!trimmed) {
    return "City is required";
  }
  if (trimmed.length < 2) {
    return "Enter a valid city name";
  }
  return undefined;
};

const validateState = (state: string): string | undefined => {
  if (!state) {
    return "State is required";
  }
  return undefined;
};

const SetupUnclaimedScreen = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<Step>(Step.Step0);

  // Form state with useState for reactivity
  const [formState, setFormState] = useState<FormState>({
    firstName: "",
    lastName: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [addresses, setAddresses] = useState<Address[]>([]);
  const { isDesktop } = useWindow();

  useEffect(() => {
    const fetchCurrentSearch = async () => {
      const response = await UnclaimedServices.getCurrentUnclaimedSearch();
      if (response.success && response.data) {
        if (response.data.names?.length > 0) {
          setFormState({
            firstName: response.data.names[0].first,
            lastName: response.data.names[0].last,
          });
        }
        setAddresses(response.data.addresses || []);
      }
    };
    fetchCurrentSearch();
  }, []);

  const handleChange = useCallback((field: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const validateStep1 = useCallback((): boolean => {
    const errors: FormErrors = {};

    const firstNameError = validateName(formState.firstName, "First name");
    if (firstNameError) errors.firstName = firstNameError;

    const lastNameError = validateName(formState.lastName, "Last name");
    if (lastNameError) errors.lastName = lastNameError;

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState]);

  const handleAddAddress = useCallback((address: Address) => {
    setAddresses((prev) => [...prev, address]);
  }, []);

  const handleRemoveAddress = useCallback((streetToRemove: string) => {
    setAddresses((prev) => prev.filter((addr) => addr.street !== streetToRemove));
  }, []);

  const stepMap = useMemo(() => {
    return {
      [Step.Step0]: <Step0 />,
      [Step.Step1]: (
        <Step1
          formState={formState}
          errors={formErrors}
          onChange={handleChange}
        />
      ),
      [Step.Step2]: (
        <Step2
          onSubmitAddress={handleAddAddress}
          addresses={addresses}
          onRemoveAddress={handleRemoveAddress}
        />
      ),
    };
  }, [formState, formErrors, handleChange, addresses, handleAddAddress, handleRemoveAddress]);

  const stepActions = useMemo(() => {
    return {
      [Step.Step0]: {
        title: "Start monitoring",
        disabled: false,
        onPress: () => {
          setStep(Step.Step1);
        },
      },
      [Step.Step1]: {
        title: "Add an address",
        disabled: false,
        onPress: () => {
          if (validateStep1()) {
            setStep(Step.Step2);
          }
        },
      },
      [Step.Step2]: {
        title: "Start the search",
        disabled: false,
        onPress: async () => {
          if (addresses.length === 0) {
            showError("Please add at least one address", "Missing Address");
            return;
          }

          setIsLoading(true);
          const response = await UnclaimedServices.submitUnclaimedSearch({
            names: [
              {
                first: formState.firstName.trim(),
                last: formState.lastName.trim(),
              },
            ],
            addresses,
          });
          setIsLoading(false);

          if (response.success) {
            router.dismissAll();
            router.replace("/(tabs)/feeds");
          } else {
            showError(response.message || "Failed to submit unclaimed search", "Submission Failed");
          }
        },
      },
    };
  }, [validateStep1, formState, addresses]);

  const currentAction = stepActions[step];

  return (
    <SafeAreaView style={[styles.container, isDesktop && styles.containerDesktop]}>
      <Header title="Setup Unclaimed" />
      <View style={styles.content}>{stepMap[step]}</View>
      <View style={styles.footer}>
        <View style={styles.footerTextContainer}>
          <Icons.ShieldCheck width={scale(24)} height={scale(24)} />
          <Text style={styles.footerText}>
            {`We do not sell your data.\nThis is only required to perform the search.`}
          </Text>
        </View>
        <ActionButton
          title={currentAction.title}
          onPress={currentAction.onPress}
          disabled={isLoading || currentAction.disabled}
        />
      </View>
    </SafeAreaView>
  );
};

const Step0 = () => {
  return (
    <>
      <Text style={styles.title}>Find unclaimed money</Text>
      <Text style={styles.description}>
        Sparrow finds unclaimed money under your name, like uncashed checks and
        tax refunds. The average user finds over $121 in unclaimed money.{" "}
      </Text>
      <Image
        source={Images.unclaimedSetupBanner}
        style={styles.banner}
        contentFit="contain"
      />
    </>
  );
};

type Step1Props = {
  formState: FormState;
  errors: FormErrors;
  onChange: (_field: keyof FormState, _value: string) => void;
};

const Step1 = ({ formState, errors, onChange }: Step1Props) => {
  return (
    <>
      <Text style={styles.title}>What&apos;s your name?</Text>
      <Text style={styles.description}>
        Sparrow will search for unclaimed checks sent to these mailing
        addresses.
      </Text>
      <Input
        placeholder="First name"
        label="First name"
        value={formState.firstName}
        required
        error={errors.firstName}
        onChangeText={(text) => onChange("firstName", text)}
        autoCapitalize="words"
        autoCorrect={false}
      />
      <Input
        placeholder="Last name"
        label="Last name"
        value={formState.lastName}
        required
        error={errors.lastName}
        onChangeText={(text) => onChange("lastName", text)}
        autoCapitalize="words"
        autoCorrect={false}
      />
    </>
  );
};

type Step2Props = {
  onSubmitAddress: (_address: Address) => void;
  addresses: Address[];
  onRemoveAddress: (_street: string) => void;
};

const Step2 = ({ onSubmitAddress, addresses, onRemoveAddress }: Step2Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [state, setState] = useState("");
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});

  const resetForm = useCallback(() => {
    setStreet("");
    setCity("");
    setZipCode("");
    setState("");
    setAddressErrors({});
  }, []);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    resetForm();
  }, [resetForm]);

  const validateAddress = useCallback((): boolean => {
    const errors: AddressErrors = {};

    const streetError = validateStreet(street);
    if (streetError) errors.street = streetError;

    const cityError = validateCity(city);
    if (cityError) errors.city = cityError;

    const stateError = validateState(state);
    if (stateError) errors.state = stateError;

    const zipError = validateZipCode(zipCode);
    if (zipError) errors.zip = zipError;

    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  }, [street, city, state, zipCode]);

  const handleAddAddress = useCallback(() => {
    if (validateAddress()) {
      onSubmitAddress({
        street: street.trim(),
        city: city.trim(),
        state,
        zip: zipCode.trim(),
      });
      handleClose();
    }
  }, [validateAddress, onSubmitAddress, street, city, state, zipCode, handleClose]);

  const handleFieldChange = useCallback(
    (field: keyof AddressErrors, value: string, setter: (_v: string) => void) => {
      setter(value);
      setAddressErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  return (
    <>
      <Text style={styles.title}>Add your mailing addresses</Text>
      <Text style={styles.description}>
        Sparrow will search for unclaimed checks sent to these mailing
        addresses.
      </Text>
      <TouchableOpacity onPress={() => setIsVisible(true)}>
        <View style={styles.addAddressButton}>
          <View style={styles.addAddressButtonIcon}>
            <Icons.Plus width={scale(16)} height={scale(16)} />
          </View>
          <Text style={styles.addAddressButtonText}>Add address</Text>
        </View>
      </TouchableOpacity>
      <FlatList
        data={addresses}
        scrollEnabled={false}
        keyExtractor={(item) => item.street + item.city + item.state + item.zip}
        renderItem={({ item }) => (
          <View style={styles.addressItem}>
            <Text style={styles.addressText}>
              {`${item.street}, ${item.city}, ${item.state}`}
            </Text>
            <TouchableOpacity onPress={() => onRemoveAddress(item.street)}>
              <AntDesign name="close" size={scale(24)} color={Colors.lightIcon} />
            </TouchableOpacity>
          </View>
        )}
        ItemSeparatorComponent={() => <View style={styles.addressSeparator} />}
        ListEmptyComponent={() => (
          <Text style={styles.emptyAddressText}>
            No addresses added yet. Add at least one address to continue.
          </Text>
        )}
      />
      <BottomSheet
        isVisible={isVisible}
        onClose={handleClose}
        title="Add address"
        scrollable
      >
        <View>
          <Input
            placeholder="Street address"
            label="Street address"
            value={street}
            error={addressErrors.street}
            onChangeText={(text) => handleFieldChange("street", text, setStreet)}
            autoCapitalize="words"
          />
          <Input
            placeholder="City"
            label="City"
            value={city}
            error={addressErrors.city}
            onChangeText={(text) => handleFieldChange("city", text, setCity)}
            autoCapitalize="words"
          />
          <View style={styles.row}>
            <Input
              placeholder="Zip code"
              label="Zip code"
              value={zipCode}
              error={addressErrors.zip}
              onChangeText={(text) => handleFieldChange("zip", text, setZipCode)}
              containerStyle={styles.flexGrow}
              keyboardType="number-pad"
              maxLength={10}
            />
            <View style={styles.flexGrow}>
              <Select
                placeholder="State"
                label="State"
                value={state}
                options={US_STATES.map((s) => ({
                  label: s.split(",")[0],
                  value: s.split(",")[0],
                }))}
                onSelect={(value) => {
                  setState(String(value.value));
                  setAddressErrors((prev) => ({ ...prev, state: undefined }));
                }}
                containerStyle={styles.flexGrow}
              />
              {addressErrors.state && (
                <Text style={styles.selectError}>{addressErrors.state}</Text>
              )}
            </View>
          </View>
          <ActionButton
            title="Add address"
            onPress={handleAddAddress}
          />
        </View>
      </BottomSheet>
    </>
  );
};

export default SetupUnclaimedScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  containerDesktop: {
    width: 600,
    alignSelf: "center",
  },
  content: {
    flex: 1,
    padding: 20,
    gap: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: Colors.text,
  },
  description: {
    fontSize: 16,
    color: Colors.text,
    lineHeight: 24,
  },
  banner: {
    width: "100%",
    height: 400,
  },
  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 20,
    paddingBottom: 32,
    backgroundColor: "white",
    paddingTop: 16,
  },
  row: {
    flexDirection: "row",
    gap: 16,
  },
  addAddressButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  addAddressButtonText: {
    fontSize: 16,
    color: Colors.primary,
  },
  addAddressButtonIcon: {
    backgroundColor: Colors.primary,
    padding: 8,
    borderRadius: 100,
    alignItems: "center",
    justifyContent: "center",
  },
  addressItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  addressText: {
    fontSize: 16,
    color: Colors.text,
  },
  addressSeparator: {
    height: 1,
    backgroundColor: Colors.lightIcon,
    marginVertical: 16,
  },
  footerText: {
    fontSize: 14,
    color: Colors.primary,
  },
  footerTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  emptyAddressText: {
    fontSize: 14,
    color: Colors.lightIcon,
    textAlign: "center",
    marginTop: 24,
  },
  selectError: {
    color: Colors.error,
    fontSize: 12,
    marginTop: -12,
    marginBottom: 16,
  },
  flexGrow: {
    flexGrow: 1,
  },
});
